import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { isoDate } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Input, Select, Textarea, useToast } from '../components/ui';

/** Route: `/expenses/new`. */
export function ExpenseFormPage() {
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    expenseDate: isoDate(),
    categoryId: '',
    amount: 0,
    description: '',
    vendorName: '',
    paymentMethod: '',
    receiptNumber: '',
  });

  const { data: categories } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get<any[]>('/expense-categories'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/expenses', {
        ...form,
        vendorName: form.vendorName || undefined,
        paymentMethod: form.paymentMethod || undefined,
        receiptNumber: form.receiptNumber || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      navigate('/expenses');
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('expenses.new')}
      backTo="/expenses"
      backLabel={t('expenses.title')}
      submitLabel={t('common.create')}
      submitting={create.isPending}
      disabled={!form.categoryId || !form.description || form.amount <= 0}
      onSubmit={() => create.mutate()}
    >
      <FormGrid>
        <Field label={t('common.date')} required>
          <Input
            type="date"
            value={form.expenseDate}
            onChange={(e) => setForm({ ...form, expenseDate: e.target.value })}
            autoFocus
          />
        </Field>
        <Field label={t('expenses.categories')} required>
          <Select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
            <option value="">—</option>
            {(categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>{localized(c)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.amount')} required>
          <Input
            type="number"
            min={0.01}
            step="0.01"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
          />
        </Field>
        <Field label={t('expenses.vendor')}>
          <Input value={form.vendorName} onChange={(e) => setForm({ ...form, vendorName: e.target.value })} />
        </Field>
        <Field label={t('common.description')} required className="sm:col-span-2">
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label={t('payments.method')}>
          <Select value={form.paymentMethod} onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}>
            <option value="">—</option>
            {['CASH', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'ONLINE'].map((m) => (
              <option key={m} value={m}>{m.replace('_', ' ')}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('expenses.receipt')}>
          <Input value={form.receiptNumber} onChange={(e) => setForm({ ...form, receiptNumber: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/** Route: `/expenses/categories/new`. */
export function ExpenseCategoryFormPage() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', nameAr: '', code: '', description: '' });

  const create = useMutation({
    mutationFn: () => api.post('/expense-categories', { ...form, nameAr: form.nameAr || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      navigate('/expenses');
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('expenses.categories')}
      backTo="/expenses"
      backLabel={t('expenses.title')}
      submitLabel={t('common.create')}
      submitting={create.isPending}
      disabled={!form.name || !form.code}
      onSubmit={() => create.mutate()}
    >
      <FormGrid>
        <Field label={`${t('common.name')} (EN)`} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={`${t('common.name')} (AR)`}>
          <Input dir="rtl" value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('common.code')} required>
          <Input
            dir="ltr"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
          />
        </Field>
      </FormGrid>
    </FormPage>
  );
}
